import React, { useEffect, useState } from 'react'
import styled from '@emotion/styled'
import { SearchModal } from './SearchModal'
import { useAppSelector } from '@/app/hooks'
import { selectFilter } from '@/app/reducers/filters'

interface MobileMapHeaderProps {
	title: string
	count: number
	onBack: () => void
	onOpenFilters: () => void
	tipoOperacionFamilia?: string
	tipoPropiedadFamilia?: string
}

const SEARCH_INVITE_TEXT = 'Busca por ubicación, comuna o tipo de propiedad'

const HamburgerIcon = () => (
	<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
		<line x1="3" y1="6" x2="21" y2="6" />
		<line x1="3" y1="12" x2="21" y2="12" />
		<line x1="3" y1="18" x2="21" y2="18" />
	</svg>
)

const SearchIconGray = () => (
	<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ color: '#999999', flexShrink: 0 }}>
		<path d="m21 21-4.34-4.34" />
		<circle cx="11" cy="11" r="8" />
	</svg>
)

const SearchIconWhite = () => (
	<svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ color: '#ffffff' }}>
		<path d="m21 21-4.34-4.34" />
		<circle cx="11" cy="11" r="8" />
	</svg>
)

const FilterIcon = () => (
	<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
		<polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
	</svg>
)

export const MobileMapHeader = ({
	title: _title,
	count: _count,
	onBack: _onBack,
	onOpenFilters,
	tipoOperacionFamilia = '',
	tipoPropiedadFamilia = '',
}: MobileMapHeaderProps) => {
	const [searchOpen, setSearchOpen] = useState(false)
	const filterState = useAppSelector(selectFilter)
	const filtersCount = filterState.filtros?.length ?? 0

	// Precarga el texto del pill con el resultado guardado por el buscador (clásico primero, luego IA)
	const [classicLabel, setClassicLabel] = useState<string | null>(null)
	const [iaLabel, setIaLabel] = useState<string | null>(null)

	useEffect(() => {
		try {
			const rawAddress = localStorage.getItem('toctocSearchAddress')
			const savedAddress = rawAddress ? JSON.parse(rawAddress) : null
			if (savedAddress?.label) setClassicLabel(savedAddress.label)

			const rawIa = localStorage.getItem('toctocSearchIa')
			const savedIa = rawIa ? JSON.parse(rawIa) : null
			if (savedIa?.label) setIaLabel(savedIa.label)
		} catch {
			// localStorage no disponible o con contenido corrupto: se ignora
		}
	}, [])

	const searchLabel = classicLabel || iaLabel || SEARCH_INVITE_TEXT

	const openDSMenu = () => {
		// El DS renderiza el label en un <span> interno (no como aria-label), buscamos por textContent
		const dsHeader = Array.from(document.querySelectorAll('header')).find(header =>
			Array.from(header.querySelectorAll('button')).some(btn => btn.textContent?.includes('navegación mobile'))
		)
		const burger = dsHeader && Array.from(dsHeader.querySelectorAll('button')).find(
			btn => btn.textContent?.includes('navegación mobile')
		) as HTMLButtonElement | undefined
		burger?.click()

		// Workaround: el panel deslizable del menú (@toctoc/design-system) puede quedar
		// con una medición de layout obsoleta (height:0) tras el toggle. Forzamos un
		// reflow real alternando display, no basta con reasignar el mismo valor de height.
		requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				dsHeader?.querySelectorAll<HTMLElement>(':scope > div').forEach(el => {
					if (getComputedStyle(el).position !== 'absolute') return
					const prevDisplay = el.style.display
					el.style.display = 'none'
					void el.offsetHeight
					el.style.display = prevDisplay
				})
			})
		})
	}

	return (
		<>
			<Header>
				<BackBtn onClick={openDSMenu} aria-label="Menú">
					<HamburgerIcon />
				</BackBtn>

				<SearchPill onClick={() => setSearchOpen(true)} aria-label="Abrir buscador">
					<SearchIconGray />
					<SearchPillText>{searchLabel}</SearchPillText>
					<SearchPillAction>
						<SearchIconWhite />
					</SearchPillAction>
				</SearchPill>

				<FilterBtn onClick={onOpenFilters} aria-label="Abrir filtros" data-active={filtersCount > 0 ? 'true' : undefined}>
					<FilterIcon />
					{filtersCount > 0 && <FilterDot />}
				</FilterBtn>
			</Header>

			<SearchModal
				open={searchOpen}
				onClose={() => setSearchOpen(false)}
				tipoOperacionFamilia={tipoOperacionFamilia}
				tipoPropiedadFamilia={tipoPropiedadFamilia}
			/>
		</>
	)
}

/* ─── Styled Components ─────────────────────────────────────── */

const Header = styled.header`
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	height: calc(64px + env(safe-area-inset-top));
	padding: env(safe-area-inset-top) 10px 0 10px;
	background: #ffffff;
	border-bottom: 1px solid #e5e5e5;
	display: flex;
	align-items: center;
	gap: 8px;
	z-index: 350;
	backface-visibility: hidden;

	@media (min-width: 768px) {
		display: none;
	}
`

const BackBtn = styled.button`
	width: 40px;
	height: 40px;
	flex-shrink: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 10px;
	border: 1px solid #e5e5e5;
	background: #ffffff;
	cursor: pointer;
	color: #343a40;

	&:active {
		background: #f5f5f5;
	}
`

const SearchPill = styled.button`
	flex: 1;
	min-width: 0;
	display: flex;
	align-items: center;
	gap: 7px;
	height: 38px;
	padding: 0 12px;
	border-radius: 10px;
	border: 1.5px solid #e5e5e5;
	background: #f9f9f9;
	cursor: pointer;
	text-align: left;
	font-family: Nunito, sans-serif;
	overflow: hidden;

	&:active {
		background: #f0f0f0;
	}
`

const SearchPillText = styled.span`
	font-size: 13px;
	font-weight: 600;
	color: #343a40;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	flex: 1;
`

const SearchPillAction = styled.div`
	flex-shrink: 0;
	width: 26px;
	height: 26px;
	border-radius: 7px;
	background: #3200c1;
	display: flex;
	align-items: center;
	justify-content: center;
`

const FilterBtn = styled.button`
	position: relative;
	width: 40px;
	height: 40px;
	flex-shrink: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 10px;
	border: 1.5px solid #e5e5e5;
	background: #ffffff;
	cursor: pointer;
	color: #343a40;

	&[data-active="true"] {
		border-color: #3200c1;
		background: #eaf2fc;
		color: #3200c1;
	}

	&:active {
		background: #f0f0f0;
	}
`

const FilterDot = styled.span`
	position: absolute;
	top: 7px;
	right: 7px;
	width: 7px;
	height: 7px;
	border-radius: 50%;
	background: #3200c1;
	border: 1.5px solid #ffffff;
	pointer-events: none;
`
